import RadioGroupContainer from '@/components/RadioGroupContainer/RadioGroupContainer';
import { Column, Row } from '@/components/SettingsLayout';
import { useGetUser } from '@/hooks';
import { usePaginationFilter } from '@/hooks/usePaginationFilter';
import { useGetBuildsAndInstalledPresetsQuery } from '@/store/slices/checkOutFlowApiSlice';
import { BuildPaginationFilter, TypeOfBuild } from '@/types/build.type';
import { Text } from '@/ui';
import RadioInput from '@/ui/RadioInput/RadioInput';
import { skipToken } from '@reduxjs/toolkit/query';
import { Controller, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import CheckOutGridInFormContext from './CheckOutGrid';
import styles from './styles.module.css';

const DEFAULT_FILTER_CONFIG = {
  limit: 10,
  offset: 0,
  search: '',
  buildType: TypeOfBuild.BUILD,
  isPublished: false,
  includeNeverPublishedBuilds: false,
};

function CheckoutFlowsAccess() {
  const { t } = useTranslation();
  const { control, watch } = useFormContext();
  const checkoutFlowsValue = watch('checkoutFlows');
  const { id } = useParams();
  const { company } = useGetUser();
  const { currentUser } = useGetUser();

  const { page, setPage, filter, perPage, setPerPage } =
    usePaginationFilter<BuildPaginationFilter>(DEFAULT_FILTER_CONFIG);

  const { data: combinedResponse, isLoading: buildsAndPresetsLoading } =
    useGetBuildsAndInstalledPresetsQuery(
      id && company
        ? {
            companyId: company._id,
            filters: filter,
            isPrintBlocks: false,
            organizationId: currentUser.companyId,
          }
        : skipToken,
    );
  const allBuilds = combinedResponse?.data || [];
  const totalCount = combinedResponse?.count || 0;

  if (buildsAndPresetsLoading || !allBuilds.length) {
    return null;
  }

  return (
    <Column gapSize='12' fullWidth>
      <Text size='l' weight='medium'>
        {t('Restrictions.checkout_flows_access')}
      </Text>
      <Column gapSize='8' fullWidth>
        <Text size='m' weight='light-medium'>
          {t('Restrictions.checkout_flows')}
        </Text>
        <Column gapSize='4' fullWidth>
          <Controller
            name='checkoutFlows'
            control={control}
            render={({ field: { value, onChange } }) => (
              <RadioGroupContainer fullWidth>
                <Row fullWidth justify='justify-between'>
                  <Column gapSize='4'>
                    <RadioInput
                      id='unlimited'
                      checked={value === null}
                      onChange={() => onChange(null)}
                      label={t('Restrictions.all')}
                      testId='checkout-flows-restrictions-radio-button'
                    />
                  </Column>
                </Row>
                <Row fullWidth justify='justify-between'>
                  <Column gapSize='4'>
                    <RadioInput
                      id='limited'
                      checked={Array.isArray(value)}
                      onChange={() => onChange([])}
                      label={t('Restrictions.limited')}
                      testId='checkout-flows-restrictions-radio-button-limited'
                    />
                    <div className={styles.restrictionComment}>
                      <Text size='xs' weight='light-medium' color='base-grey'>
                        {t('Restrictions.checkout_flows_comment')}
                      </Text>
                    </div>
                  </Column>
                </Row>
              </RadioGroupContainer>
            )}
          />
          {Array.isArray(checkoutFlowsValue) && (
            <CheckOutGridInFormContext
              builds={allBuilds}
              totalCount={totalCount}
              page={page}
              setPage={setPage}
              perPage={perPage}
              setPerPage={setPerPage}
            />
          )}
        </Column>
      </Column>
    </Column>
  );
}

export default CheckoutFlowsAccess;
